<template>
    <div class="rounded-0 performance-table-wrapper">
        <div class="dash-card">
            <div class="table-header d-flex align-items-center justify-content-between mb-3">
                <div class="d-flex justify-content-between align-items-center gap-3 w-100">
                    <h5 class="table-title mb-1">{{ title }}</h5>
                    <span class="table-subtitle">Showing {{ displayData.length }} {{ internalViewType === 'top' ? 'top' : 'least' }} performers</span>
                    <select 
                        v-model="internalViewType" 
                        @change="onViewTypeChange" 
                        class="form-control form-control-sm performance-selector"
                    >
                        <option value="top" class="small fw-normal text-muted">Top Performing</option>
                        <option value="least" class="small fw-normal text-muted">Least Performing</option>
                    </select>
                </div>
                
                <div class="d-flex align-items-center gap-2">
                    <div class="date-range-filter" v-if="endpoint && enableDateFilter">
                        <i class="fa fa-calendar-alt text-muted me-2"></i>
                        <select v-model="internalDateRange" @change="fetchData" class="form-select form-select-sm">
                            <option value="today">Today</option>
                            <option value="7_days">7 Days</option>
                            <option value="30_days">30 Days</option>
                            <option value="all_time">All Time</option>
                        </select>
                    </div>
                </div>
            </div>
            

            <div v-if="loading" class="text-center py-5">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>

            	<!-- Empty State -->
			<div v-else-if="!loading && displayData.length === 0" class="text-center py-5">
				<i class="fa fa-inbox text-muted mb-3" style="font-size: 3rem;"></i>
				<p class="text-muted">No performance data available</p>
			</div>

            <div v-else class="table-responsive">
                <table class="table table-hover">
                    <thead class="text-muted small">
                        <tr class="text-muted small  fw-normal">
                            <th>
                                <span class="small text-muted fw-normal">#</span> 
                            </th>
                            <th> <span class="small text-muted fw-normal">Rider</span></th>
                            <th><span class="small text-muted fw-normal">Engine No.</span></th>
                            <th><span class="small text-muted fw-normal">Claims</span></th>
                            <th><span class="small text-muted fw-normal">Revenue</span></th>
                         
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in displayData" :key="item.id || index" class="small">
                            <td>
                                <span class="rank-badge" :class="getRankClass(index)">
                                    {{ index + 1 }}
                                </span>
                            </td>
                            <td><span class="small text-muted fw-normal">{{ item.rider_name }}</span></td>
                            <td><span class="small text-muted fw-normal  text-xs">{{ item.engine_number }}</span></td>
                            <td><span class="small text-muted fw-normal">{{ item.claims_count || 0 }}</span></td>
                            <td>
                                <span class="small text-muted fw-normal" :class="getRevenueClass(item.revenue)">
                                    {{ item.revenue }}
                                </span>
                            </td>
                           
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative, Watch } from 'vue-facing-decorator'

@Component({})
class PerformanceTable extends Vue {
    loading = false
    fetchedData: any[] = []
    internalDateRange = 'today'
    internalViewType = 'top' // Local state for the dropdown

    @Prop({ type: String, required: true }) readonly title!: string
    @Prop({ type: String, default: '' }) readonly endpoint!: string
    @Prop({ type: Object, default: () => ({ top: [], least: [] }) }) 
    readonly performanceData!: { top: any[], least: any[] }
    @Prop({ type: Boolean, default: true }) readonly enableDateFilter!: boolean

    // Inside PerformanceTable.vue script

    @Watch('internalViewType')
    onViewTypeChange() {
        // If we are in "Endpoint" mode, we need to re-fetch from the server
        if (this.endpoint) {
            this.fetchData();
        }
        // If we are in "Prop" mode, displayData (computed) will 
        // update automatically because internalViewType changed.
    }

    get displayData() {
        // 1. Check if we have data passed via props
        const hasPropData = (this.performanceData.top?.length > 0 || this.performanceData.least?.length > 0);
        
        if (hasPropData) {
            // Toggle between the two arrays provided by the parent
            return this.internalViewType === 'top' 
                ? this.performanceData.top 
                : this.performanceData.least;
        }
        
        // 2. Fallback to data fetched from the endpoint
        return this.fetchedData;
    }

    async fetchData() {
        if (!this.endpoint) return
        this.loading = true
        try {
            const { data } = await this.$http.get(this.endpoint, {
                params: { type: this.internalViewType, date_range: this.internalDateRange }
            })
            this.fetchedData = data.data || data || []
        } catch (e) { console.error(e) }
        finally { this.loading = false }
    }

    getRankClass(index: number) {
        if (this.internalViewType === 'least') return 'rank-default'
        if (index === 0) return 'rank-gold'
        if (index === 1) return 'rank-silver'
        if (index === 2) return 'rank-bronze'
        return 'rank-default'
    }

    formatCurrency(val: number) {
        return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(val || 0)
    }

    getRevenueClass(revenue: number) {
        if (this.internalViewType === 'least') return 'revenue-low'
        return revenue >= 50000 ? 'revenue-high' : 'revenue-low'
    }
}
export default toNative(PerformanceTable)
</script>


<style lang="scss" scoped>
    .performance-table-wrapper {
        margin-bottom: 1.5rem;
    }
    .performance-selector {
    width: auto;
    font-weight: 600;
    border-color: #e5e7eb;
    color: #4b5563;
    background-color: #f9fafb;
}
    .dash-card {
        background: #fff;
        border: 1px solid #e5e7eb;
        
        padding: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: box-shadow 0.2s;
    
        &:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
    }
    
    /* Header */
    .table-header {
        .table-title {
            font-size: 1.1rem;
            font-weight: 700;
            color: #111827;
            margin: 0;
        }
    
        .table-subtitle {
            font-size: 0.85rem;
            color: #6b7280;
        }
    }
    
    .date-range-filter {
        display: flex;
        align-items: center;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
    
        padding: 0.4rem 0.75rem;
    
        .form-select {
            border: none;
            background: transparent;
            font-size: 0.85rem;
            padding: 0;
            width: auto;
            cursor: pointer;
    
            &:focus {
                box-shadow: none;
            }
        }
    }
    
    /* Table */
    .performance-table {
        margin-bottom: 0;
        font-size: 0.9rem;
    
        thead {
            background: #f9fafb;
            border-bottom: 2px solid #e5e7eb;
    
            th {
                font-weight: 600;
                color: #374151;
                font-size: 0.85rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                padding: 0.75rem 1rem;
                border: none;
            }
        }
    
        tbody {
            tr {
                border-bottom: 1px solid #f3f4f6;
                transition: background-color 0.15s ease;
    
                &:hover {
                    background-color: #f9fafb;
                }
    
                &:last-child {
                    border-bottom: none;
                }
            }
    
            td {
                padding: 1rem;
                vertical-align: middle;
                color: #374151;
            }
        }
    }
    
    /* Rank Badge */
    .rank-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        font-weight: 700;
      
    
        &.rank-gold {
            background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
            color: #92400e;
            box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
        }
    
        &.rank-silver {
            background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
            color: #374151;
            box-shadow: 0 2px 8px rgba(192, 192, 192, 0.3);
        }
    
        &.rank-bronze {
            background: linear-gradient(135deg, #cd7f32 0%, #e89b5f 100%);
            color: #fff;
            box-shadow: 0 2px 8px rgba(205, 127, 50, 0.3);
        }
    
        &.rank-default {
            background: #f3f4f6;
            color: #6b7280;
        }
    }
    
    /* Rider Info */
    .rider-info {
        .rider-name {
           
            color: #111827;
        }
    }
    
    /* Engine Number */
    .engine-number {
        font-family: 'Monaco', 'Courier New', monospace;
        
        color: #6b7280;
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
    }
    
    /* Claims Count */
    .claims-count {
        font-weight: 600;
        color: #374151;
    }
    
    /* Revenue Amount */
    .revenue-amount {
        font-weight: 700;
        font-size: 0.95rem;
    
        &.revenue-high {
            color: #15803d;
        }
    
        &.revenue-medium {
            color: #1e40af;
        }
    
        &.revenue-low {
            color: #6b7280;
        }
    }
    
    /* Action Button */
    .action-btn {
        padding: 0.35rem 0.75rem;
        font-size: 0.85rem;
        border-radius: 6px;
        font-weight: 500;
        transition: all 0.2s ease;
    
        i {
            font-size: 0.8rem;
            margin-right: 0.25rem;
        }
    
        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
        }
    }
    
    /* Table Footer */
    .table-footer {
        small {
            font-size: 0.8rem;
        }
    }
    
    /* Responsive */
    @media (max-width: 768px) {
        .performance-table {
            font-size: 0.85rem;
    
            thead th,
            tbody td {
                padding: 0.6rem;
            }
        }
    
        .rank-badge {
            width: 28px;
            height: 28px;
            font-size: 0.75rem;
        }
    
        .action-btn {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
    
            i {
                margin-right: 0;
            }
    
            span {
                display: none;
            }
        }
    }
    </style>